import React, { useState, useEffect } from 'react';
import './KnowMe.css';
import Navbar from './Navbar';
import Footer from './Footer';
import SEO from '../components/SEO';
import { collection, getDocs, getDoc, doc, query, orderBy } from 'firebase/firestore';
import { db } from '../firebase/firebase-config';
import { Link } from 'react-router-dom';
import {
  FaHeart, FaLightbulb, FaStar, FaBookOpen, FaGlobeAmericas,
  FaMusic, FaPalette, FaCode, FaLeaf, FaCompass,
  FaFeather, FaMountain, FaSmile, FaGraduationCap,
  FaCoffee, FaCamera, FaArrowRight, FaQuoteLeft,
} from 'react-icons/fa';

/* ── ICON REGISTRY ────────────────────────────────────────────── */
const ICON_MAP = {
  FaHeart, FaLightbulb, FaStar, FaBookOpen, FaGlobeAmericas,
  FaMusic, FaPalette, FaCode, FaLeaf, FaCompass,
  FaFeather, FaMountain, FaSmile, FaGraduationCap,
  FaCoffee, FaCamera,
};

function Icon({ name }) {
  const C = ICON_MAP[name] || FaHeart;
  return <C />;
}

/* ── FALLBACK PROFILE ─────────────────────────────────────────── */
const DEFAULT_PROFILE = {
  heroTitle:       "Get to Know Me",
  heroSubtitle:    "The person behind the practice — curious, driven, and deeply human",
  introHeading:    "The Person Behind the Practice",
  introParagraph1: "Beyond contracts and compliance, I'm someone who believes that great legal work starts with genuine curiosity and ends with real-world impact.",
  introParagraph2: "I bring the same precision and care to my relationships as I do to my contracts — whether I'm mentoring a junior lawyer, exploring a new city, or getting lost in a good book.",
  heroImageUrl:    "",
  accentColor1:    "#C25B2A",
  accentColor2:    "#D4AF37",
  bgColor:         "#fdf8f4",
  sectionTitle:    "What Makes Me, Me",
  ctaText:         "Let's work together",
  ctaLink:         "/work-with-me",
};

function KnowMe() {
  const [profile,   setProfile]   = useState(DEFAULT_PROFILE);
  const [sections,  setSections]  = useState([]);
  const [isVisible, setIsVisible] = useState(false);
  const [loading,   setLoading]   = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [profileSnap, sectionsSnap] = await Promise.all([
          getDoc(doc(db, "about_profile", "main")),
          getDocs(query(collection(db, "about_sections"), orderBy("order","asc"))),
        ]);
        if (profileSnap.exists()) setProfile(prev => ({ ...prev, ...profileSnap.data() }));
        setSections(sectionsSnap.docs.map(d => ({ id:d.id, ...d.data() })));
      } catch(e) { console.error(e); }
      finally {
        setLoading(false);
        setTimeout(() => setIsVisible(true), 50);
      }
    })();
  }, []);

  if (loading) {
    return (
      <>
        <Navbar />
        <section className="knowme-section">
          <div className="knowme-loading">Loading…</div>
        </section>
        <Footer />
      </>
    );
  }

  return (
    <>
      <SEO
        title="About Pooja Biswal — Contract Specialist, LLM Queen Mary London"
        description="Get to know Pooja Biswal — contract specialist with LLM (Distinction) from Queen Mary University of London, passionate about helping professionals navigate contracts with confidence."
        keywords="about Pooja Biswal, contract specialist background, LLM Queen Mary London, contract law expert UK, commercial lawyer profile"
        canonical="/know-me"
        breadcrumbs={[{ name: "Home", path: "/" }, { name: "Know Me", path: "/know-me" }]}
      />
      <Navbar />

      <section
        className="knowme-section"
        style={{
          '--accent1': profile.accentColor1,
          '--accent2': profile.accentColor2,
          '--page-bg': profile.bgColor,
        }}
      >
        <div className={`knowme-container ${isVisible ? 'visible' : ''}`}>

          {/* ── HERO ── */}
          <div className="knowme-hero">
            <div className="knowme-hero-text">
              <div className="knowme-badge">
                <FaHeart />
                <span>Personal</span>
              </div>
              <h1 className="knowme-title">{profile.heroTitle}</h1>
              <p className="knowme-subtitle">{profile.heroSubtitle}</p>
            </div>
            {profile.heroImageUrl && (
              <div className="knowme-hero-image-wrap">
                <img src={profile.heroImageUrl} alt="Prachi" className="knowme-hero-image" />
                <div className="knowme-stamp" aria-hidden="true">
                  <div className="knowme-stamp-inner">
                    <span>PP</span>
                    <span>Studio</span>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* ── DECORATIVE DIVIDER ── */}
          <div className="knowme-divider" aria-hidden="true">
            <span className="knowme-divider-line" />
            <span className="knowme-divider-gem">✦</span>
            <span className="knowme-divider-line" />
          </div>

          {/* ── INTRO ── */}
          <div className="knowme-intro">
            <FaQuoteLeft className="knowme-quote-icon" aria-hidden="true" />
            <h2 className="knowme-intro-heading">{profile.introHeading}</h2>
            <p className="knowme-intro-p">{profile.introParagraph1}</p>
            {profile.introParagraph2 && <p className="knowme-intro-p">{profile.introParagraph2}</p>}
          </div>

          {/* ── SECTION CARDS ── */}
          {sections.length > 0 && (
            <div className="knowme-cards-section">
              <div className="knowme-cards-header">
                <h2 className="knowme-cards-heading">{profile.sectionTitle}</h2>
                <div className="knowme-cards-rule" aria-hidden="true" />
              </div>
              <div className="knowme-cards-grid">
                {sections.map((s, i) => (
                  <div
                    key={s.id}
                    className={`knowme-card knowme-card-${s.type}`}
                    style={{
                      '--card-accent': s.accentColor,
                      '--card-bg':     s.bgColor,
                      '--delay':       `${i * 80}ms`,
                    }}
                  >
                    <div className="knowme-card-icon-wrap">
                      <div className="knowme-card-icon"><Icon name={s.iconKey} /></div>
                    </div>
                    <div className="knowme-card-type-badge">{s.type}</div>
                    <h3 className="knowme-card-title">{s.title}</h3>
                    {s.body && <p className="knowme-card-body">{s.body}</p>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ── CTA ── */}
          {profile.ctaText && (
            <div className="knowme-cta">
              <div className="knowme-cta-inner">
                <div className="knowme-cta-ornament" aria-hidden="true">✦</div>
                <p className="knowme-cta-label">Ready to connect?</p>
                <Link to={profile.ctaLink || "/work-with-me"} className="knowme-cta-btn">
                  {profile.ctaText}
                  <FaArrowRight className="knowme-cta-arrow" />
                </Link>
              </div>
            </div>
          )}

        </div>
      </section>

      <Footer />
    </>
  );
}

export default KnowMe;
